(() => {
  'use strict';
  if (window.SadoScenographyV39) { window.SadoScenographyV39.refresh(); return; }
  const instances = new Map();
  const scripts = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const compact = window.matchMedia('(max-height: 719px)');
  function loadScript(url, ready) {
    if (ready()) return Promise.resolve();
    if (!url) return Promise.reject(new Error('Missing animation asset'));
    if (scripts.has(url)) return scripts.get(url);
    const request = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url; script.async = true;
      let done = false;
      const finish = error => {
        if (done) return;
        done = true; clearTimeout(timer); script.onload = script.onerror = null;
        if (error) { script.remove(); reject(error); } else resolve();
      };
      const timer = setTimeout(() => finish(new Error('Animation asset timeout')), 10000);
      script.onload = () => finish(ready() ? null : new Error('Animation asset unavailable'));
      script.onerror = () => finish(new Error('Animation asset load failed'));
      document.head.append(script);
    });
    scripts.set(url, request);
    return request;
  }
  function edges(root) {
    let top = innerWidth < 900 ? 76 : 90;
    let bottom = 12;
    document.querySelectorAll('sticky-header,.shopify-section-header-sticky,[data-sa39-header],#preview-bar-iframe,#PBarNextFrame,[data-sa39-preview-bar]').forEach(el => {
      const rect = el.getBoundingClientRect();
      const style = getComputedStyle(el);
      if (!rect.width || !rect.height || style.visibility === 'hidden' || style.display === 'none') return;
      if (el.id === 'PBarNextFrame') {
        let parent = el.parentElement;
        let reserve = innerWidth < 900 ? 100 : 76;
        while (parent && parent !== document.body) {
          const clip = parent.getBoundingClientRect();
          const parentStyle = getComputedStyle(parent);
          if (parentStyle.position === 'fixed' && /hidden|clip/.test(`${parentStyle.overflow} ${parentStyle.overflowY}`) && clip.height < innerHeight / 2 && clip.top >= innerHeight / 2 && clip.bottom >= innerHeight - 4) {
            reserve = innerHeight - clip.top; break;
          }
          parent = parent.parentElement;
        }
        bottom = Math.max(bottom, reserve + 12);
      }
      else if (el.matches('#preview-bar-iframe,[data-sa39-preview-bar]') && rect.bottom >= innerHeight - 4) bottom = Math.max(bottom, innerHeight - rect.top + 12);
      else if (rect.top <= top && rect.bottom > 0 && rect.height < 180) top = Math.max(top, rect.bottom + 8);
    });
    root.style.setProperty('--sa39-top', `${Math.ceil(top)}px`);
    root.style.setProperty('--sa39-bottom', `${Math.ceil(bottom)}px`);
    return { top, bottom };
  }
  function mount(root) {
    if (instances.has(root)) return;
    const state = { disposed: false, observer: null, mm: null, loading: false, retry: null, imageCancels: [] };
    instances.set(root, state);
    const panels = [...root.querySelectorAll('[data-sa39-panel]')];
    const buttons = [...root.querySelectorAll('[data-sa39-select]')];
    const images = panels.map(panel => panel.querySelector('img'));
    const controls = root.querySelector('.sa39-controls');
    const meter = root.querySelector('.sa39-meter');
    const hint = root.querySelector('.sa39-hint');
    const stage = root.querySelector('.sa39-stage');
    const layout = root.querySelector('.sa39-layout');
    const bar = root.querySelector('[data-sa39-progress]');
    const status = root.querySelector('[data-sa39-status]');
    const eligible = () => !reduced.matches && !compact.matches;
    function imageReady(image) {
      image.loading = 'eager';
      if (image.complete) return image.naturalWidth > 0 ? Promise.resolve() : Promise.reject(new Error('Image unavailable'));
      return new Promise((resolve, reject) => {
        let done = false;
        const finish = error => { if (done) return; done = true; clearTimeout(timer); image.removeEventListener('load', loaded); image.removeEventListener('error', failed); state.imageCancels = state.imageCancels.filter(cancel => cancel !== cancelled); error ? reject(error) : resolve(); };
        const loaded = () => finish(image.naturalWidth > 0 ? null : new Error('Image unavailable'));
        const failed = () => finish(new Error('Image unavailable'));
        const cancelled = () => finish(new Error('Section unloaded'));
        const timer = setTimeout(() => finish(new Error('Image timeout')), 10000);
        state.imageCancels.push(cancelled);
        image.addEventListener('load', loaded, { once: true }); image.addEventListener('error', failed, { once: true });
      });
    }
    function reset() {
      root.classList.remove('is-enhanced');
      root.dataset.sa39State = reduced.matches ? 'reduced' : compact.matches ? 'compact' : 'static';
      delete root.dataset.sa39Phase;
      [controls, meter, hint].forEach(el => { el.hidden = true; });
      panels.forEach(panel => { panel.removeAttribute('aria-hidden'); panel.inert = false; panel.style.removeProperty('opacity'); panel.style.removeProperty('visibility'); panel.style.removeProperty('transform'); });
      buttons.forEach(button => button.setAttribute('aria-pressed', 'false'));
    }
    function activate() {
      if (state.disposed || state.mm || !eligible() || !root.isConnected) return;
      const gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger);
      const mm = gsap.matchMedia();
      state.mm = mm;
      mm.add({ tall: '(min-height: 720px)', reduce: '(prefers-reduced-motion: reduce)' }, context => {
        if (!context.conditions.tall || context.conditions.reduce) { reset(); return; }
        edges(root);
        root.classList.add('is-enhanced'); root.dataset.sa39State = 'active';
        [controls, meter, hint].forEach(el => { el.hidden = false; });
        let active = -1;
        const choose = index => {
          if (active === index) return;
          active = index; root.dataset.sa39Phase = String(index);
          panels.forEach((panel, i) => { panel.setAttribute('aria-hidden', String(i !== index)); panel.inert = i !== index; });
          buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
          status.textContent = `0${index + 1} / 03 · ${buttons[index].textContent.trim()}`;
        };
        const sync = progress => { choose(progress < .34 ? 0 : progress < .67 ? 1 : 2); root.dataset.sa39Progress = String(Math.round(progress * 100)); };
        gsap.set(panels, { autoAlpha: 0, y: 0 });
        gsap.set(panels[0], { autoAlpha: 1 }); choose(0);
        const tl = gsap.timeline({ scrollTrigger: {
          trigger: layout,
          start: () => `top top+=${edges(root).top}`,
          end: () => `+=${Math.max(500, layout.offsetHeight - stage.offsetHeight)}`,
          scrub: .24, invalidateOnRefresh: true,
          onRefreshInit: () => edges(root),
          onRefresh: self => sync(self.animation?.progress() ?? self.progress)
        }, onUpdate() { sync(this.progress()); } });
        tl.to(panels[0], { autoAlpha: 0, y: -28, duration: .16, ease: 'none' }, .26)
          .fromTo(panels[1], { autoAlpha: 0, y: 32 }, { autoAlpha: 1, y: 0, duration: .16, ease: 'none', immediateRender: false }, .26)
          .to(panels[1], { autoAlpha: 0, y: -28, duration: .16, ease: 'none' }, .59)
          .fromTo(panels[2], { autoAlpha: 0, y: 32 }, { autoAlpha: 1, y: 0, duration: .16, ease: 'none', immediateRender: false }, .59)
          .to(bar, { scaleX: 1, duration: 1, ease: 'none' }, 0);
        const handlers = buttons.map((button, index) => {
          const handler = () => { const trigger = tl.scrollTrigger; window.scrollTo({ top: Math.max(0, trigger.start + (trigger.end - trigger.start) * [.06, .5, .94][index]), behavior: 'smooth' }); };
          button.addEventListener('click', handler); return [button, handler];
        });
        let refreshFrame = 0;
        let previewDisposed = false;
        const previewObserver = 'ResizeObserver' in window ? new ResizeObserver(() => {
          if (previewDisposed) return;
          cancelAnimationFrame(refreshFrame);
          refreshFrame = requestAnimationFrame(() => { if (!previewDisposed && !state.disposed && root.isConnected) tl.scrollTrigger.refresh(); });
        }) : null;
        const previewNodes = new Set([document.getElementById('PBarNextFrame')?.parentElement, ...document.querySelectorAll('#preview-bar-iframe,[data-sa39-preview-bar]')]);
        previewNodes.forEach(node => { if (node) previewObserver?.observe(node); });
        tl.scrollTrigger.refresh();
        return () => { previewDisposed = true; previewObserver?.disconnect(); cancelAnimationFrame(refreshFrame); handlers.forEach(([button, handler]) => button.removeEventListener('click', handler)); reset(); };
      }, root);
    }
    async function prepare() {
      if (state.disposed || state.loading || state.mm) return;
      if (!eligible()) { reset(); return; }
      if (panels.length !== 3 || images.some(image => !image)) { root.dataset.sa39State = 'missing-image'; return; }
      state.loading = true;
      try {
        await Promise.all(images.map(imageReady));
        if (state.disposed) return;
        await loadScript(root.dataset.sa39Gsap, () => Boolean(window.gsap));
        if (state.disposed) return;
        await loadScript(root.dataset.sa39Trigger, () => Boolean(window.ScrollTrigger));
        if (!state.disposed) activate();
      } catch (_) { if (!state.disposed) { reset(); root.dataset.sa39State = images.some(image => !image.complete || !image.naturalWidth) ? 'image-failure' : 'library-failure'; } }
      finally { state.loading = false; }
    }
    reset();
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { state.observer.disconnect(); prepare(); } }, { rootMargin: '600px 0px' });
      state.observer.observe(root);
    } else prepare();
    state.retry = () => { if (!state.mm && eligible() && root.getBoundingClientRect().top < innerHeight + 600) prepare(); };
    reduced.addEventListener('change', state.retry); compact.addEventListener('change', state.retry);
    state.cleanup = () => { state.disposed = true; state.observer?.disconnect(); [...state.imageCancels].forEach(cancel => cancel()); state.mm?.revert(); reset(); reduced.removeEventListener('change', state.retry); compact.removeEventListener('change', state.retry); root.style.removeProperty('--sa39-top'); root.style.removeProperty('--sa39-bottom'); };
  }
  function unmount(root) { const state = instances.get(root); if (state) { state.cleanup(); instances.delete(root); } }
  function roots(node) { return [...(node.matches?.('[data-sa39-story]') ? [node] : []), ...node.querySelectorAll('[data-sa39-story]')]; }
  function refresh() { instances.forEach((_, root) => { if (!root.isConnected) unmount(root); }); roots(document).forEach(mount); }
  const onLoad = event => roots(event.target).forEach(mount);
  const onUnload = event => roots(event.target).forEach(unmount);
  document.addEventListener('shopify:section:load', onLoad); document.addEventListener('shopify:section:unload', onUnload);
  window.SadoScenographyV39 = { refresh, count: () => instances.size, destroy() { [...instances.keys()].forEach(unmount); document.removeEventListener('shopify:section:load', onLoad); document.removeEventListener('shopify:section:unload', onUnload); delete window.SadoScenographyV39; } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true }); else refresh();
})();
